// Kenya — Mbothe (Pokomo) engine.
// 2 rows × 10 pits. 2 seeds in every pit at start (40 total).
//
// Rules:
//   - Sow counter-clockwise through both rows.
//   - Two-counter protection: opponent pits containing EXACTLY 2 seeds are
//     skipped during sowing.
//   - You normally cannot start from one of YOUR OWN pits that contains
//     exactly 2 seeds; when every own pit has exactly 2, you must start from
//     the rightmost pit of your row.
//   - Relay sowing continues until landing on an empty legal pit.
//   - Capture: final seed lands in an empty pit on your OWN row AND the
//     opponent pit directly opposite contains EXACTLY 2 seeds → take those 2.
//     Capturing player plays again.
//   - Feeding rule: when opponent row is empty, you must play a move that
//     feeds the opponent when one exists.
//   - End: when a player cannot move, the opponent collects all remaining
//     on-board seeds. Highest capture total wins.

export const RULES_VERSION = "2026.02.18";
export const RULESET_ID = "ke-mbothe-standard";
export const ROWS = 2;
export const COLS = 10;
export const PLAYER_A = "A";
export const PLAYER_B = "B";

// row 0 = B (top), row 1 = A (bottom).
// CCW route across both rows.
const ROUTE = [
  [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7], [1, 8], [1, 9],
  [0, 9], [0, 8], [0, 7], [0, 6], [0, 5], [0, 4], [0, 3], [0, 2], [0, 1], [0, 0],
];

function routeIndex(r, c) {
  for (let i = 0; i < ROUTE.length; i++) if (ROUTE[i][0] === r && ROUTE[i][1] === c) return i;
  return -1;
}
function ownsRow(p, row) { return p === PLAYER_A ? row === 1 : row === 0; }
function emptyBoard() { return Array.from({ length: ROWS }, () => Array(COLS).fill(0)); }
function cloneBoard(b) { return b.map((r) => r.slice()); }

export function createInitialState() {
  const board = emptyBoard();
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) board[r][c] = 2;
  return {
    rulesetId: RULESET_ID,
    rulesVersion: RULES_VERSION,
    board,
    currentPlayer: PLAYER_A,
    phase: "NORMAL",
    captures: { A: 0, B: 0 },
    status: "ACTIVE",
    winner: null,
    endReason: null,
    turnNumber: 1,
    moveNumber: 0,
    history: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function countersOnRow(board, p) {
  const row = p === PLAYER_A ? 1 : 0;
  let s = 0; for (let c = 0; c < COLS; c++) s += board[row][c]; return s;
}

// Sow with two-counter protection. Returns { board, landing, sownCount }.
function simulateSowing(board, player, startR, startC) {
  const b = cloneBoard(board);
  const opp = player === PLAYER_A ? PLAYER_B : PLAYER_A;
  const oppRow = opp === PLAYER_A ? 1 : 0;
  const count = b[startR][startC];
  b[startR][startC] = 0;
  let idx = routeIndex(startR, startC);
  let remaining = count;
  let last = [startR, startC];
  let safety = 0;
  while (remaining > 0) {
    idx = (idx + 1) % ROUTE.length;
    const [nr, nc] = ROUTE[idx];
    // Skip OPPONENT pits that contain exactly 2 seeds.
    if (nr === oppRow && b[nr][nc] === 2) { safety++; if (safety > 200) break; continue; }
    b[nr][nc] += 1;
    last = [nr, nc];
    remaining -= 1;
  }
  return { board: b, landing: last, sownCount: count };
}

function ownPits(board, p) {
  const row = p === PLAYER_A ? 1 : 0;
  const out = [];
  for (let c = 0; c < COLS; c++) if (board[row][c] > 0) out.push([row, c]);
  return out;
}

export function getLegalMoves(state) {
  const { board, currentPlayer } = state;
  const opp = currentPlayer === PLAYER_A ? PLAYER_B : PLAYER_A;
  const myRow = currentPlayer === PLAYER_A ? 1 : 0;
  const all = ownPits(board, currentPlayer);
  if (all.length === 0) return [];
  const non2 = all.filter(([r, c]) => board[r][c] !== 2);
  // If at least one non-2 pit exists, you must use one of those.
  // Otherwise (every own pit == 2 or 0), you must start from the rightmost own pit (highest col index with seeds).
  let candidates = non2.length > 0 ? non2 : (() => {
    let rightmost = -1;
    for (let c = COLS - 1; c >= 0; c--) {
      if (board[myRow][c] > 0) { rightmost = c; break; }
    }
    return rightmost === -1 ? [] : [[myRow, rightmost]];
  })();
  // Feeding rule
  const oppSeeds = countersOnRow(board, opp);
  if (oppSeeds === 0) {
    const feed = candidates.filter(([r, c]) => {
      const { board: post } = simulateSowing(board, currentPlayer, r, c);
      return countersOnRow(post, opp) > 0;
    });
    if (feed.length > 0) candidates = feed;
  }
  return candidates;
}

export function isLegalMove(state, r, c) {
  return getLegalMoves(state).some(([rr, cc]) => rr === r && cc === c);
}

export function applyMove(state, r, c) {
  if (state.status !== "ACTIVE") throw new Error("GAME_NOT_ACTIVE");
  if (!ownsRow(state.currentPlayer, r)) throw new Error("NOT_OWN_PIT");
  if (!isLegalMove(state, r, c)) throw new Error("ILLEGAL_MOVE");

  const player = state.currentPlayer;
  const opp = player === PLAYER_A ? PLAYER_B : PLAYER_A;
  const myRow = player === PLAYER_A ? 1 : 0;
  const oppRow = opp === PLAYER_A ? 1 : 0;

  const events = [];
  events.push({ type: "TURN_STARTED", player, turn: state.turnNumber });

  let board = state.board;
  let landing = [r, c];
  let totalCaptured = 0;
  let captureChain = [];
  let plays = 0;

  // Mbothe: relay-sowing continues until you land in an empty pit. We handle
  // multi-relay by repeatedly sowing from the landing pit (if non-empty) until
  // landing in an empty pit.
  let startR = r, startC = c;
  while (true) {
    const sim = simulateSowing(board, player, startR, startC);
    board = sim.board;
    landing = sim.landing;
    events.push({ type: "SOWING_COMPLETE", from: [startR, startC], landing, sown: sim.sownCount });
    plays += 1;
    if (plays > 50) break; // safety
    const [lr, lc] = landing;
    // If landing pit was previously empty (= now 1) and is a legal continuing pit, the move ends.
    // Mbothe relay sowing: when last counter lands in an OCCUPIED pit, pick up and continue.
    // Here, board[lr][lc] === 1 means it was empty before — end of move.
    if (board[lr][lc] === 1) break;
    // Otherwise, continue relay from this pit.
    startR = lr; startC = lc;
  }

  // Capture: landed in empty pit on OWN row, opposite (same column on opp row) contains exactly 2.
  const [lr, lc] = landing;
  if (lr === myRow && board[lr][lc] === 1) {
    if (board[oppRow][lc] === 2) {
      board = cloneBoard(board);
      events.push({ type: "CAPTURE_TRIGGERED", at: landing });
      captureChain.push([oppRow, lc]);
      board[oppRow][lc] = 0;
      totalCaptured += 2;
      events.push({ type: "COUNTERS_CAPTURED", from: [oppRow, lc], count: 2 });
    }
  }

  const captures = { A: state.captures.A, B: state.captures.B };
  captures[player] += totalCaptured;

  // Capturing player plays again only when a capture occurred.
  const nextPlayer = totalCaptured > 0 ? player : opp;

  // End-condition: if next player has no legal move, opponent collects remaining board seeds.
  let status = "ACTIVE";
  let winner = null;
  let endReason = null;
  const probe = { ...state, board, currentPlayer: nextPlayer, captures };
  const nextLegal = getLegalMoves(probe);
  if (nextLegal.length === 0) {
    // Award remaining seeds to the player on whose row each seed sits → other player.
    const otherPlayer = nextPlayer === PLAYER_A ? PLAYER_B : PLAYER_A;
    const remaining = countersOnRow(board, PLAYER_A) + countersOnRow(board, PLAYER_B);
    captures[otherPlayer] += remaining;
    board = emptyBoard();
    events.push({ type: "REMAINING_SEEDS_AWARDED", to: otherPlayer, count: remaining });
    if (captures.A === captures.B) {
      status = "DRAWN"; endReason = "DRAW_BY_NO_MOVE";
    } else {
      status = "WON";
      winner = captures.A > captures.B ? PLAYER_A : PLAYER_B;
      endReason = "OPPONENT_NO_LEGAL_MOVE";
    }
    events.push({ type: status === "WON" ? "GAME_WON" : "GAME_DRAWN", player: winner, reason: endReason });
  }

  events.push({ type: "TURN_ENDED", player, landingPit: landing, captured: totalCaptured });

  const historyEntry = {
    player,
    from: [r, c],
    landingPit: landing,
    captured: totalCaptured,
    capturedPits: captureChain,
    relays: plays - 1,
    prevBoard: state.board,
    prevCaptures: state.captures,
    prevPhase: state.phase,
    prevCurrentPlayer: state.currentPlayer,
    prevStatus: state.status,
    prevWinner: state.winner,
    prevEndReason: state.endReason,
    prevTurnNumber: state.turnNumber,
    events,
  };

  return {
    state: {
      ...state,
      board,
      currentPlayer: status === "ACTIVE" ? nextPlayer : state.currentPlayer,
      captures,
      status,
      winner,
      endReason,
      turnNumber: status === "ACTIVE" ? state.turnNumber + 1 : state.turnNumber,
      moveNumber: state.moveNumber + 1,
      history: [...state.history, historyEntry],
      updatedAt: Date.now(),
    },
    events,
  };
}

export function undoMove(state) {
  if (!state.history.length) return state;
  const last = state.history[state.history.length - 1];
  return {
    ...state,
    board: last.prevBoard,
    currentPlayer: last.prevCurrentPlayer,
    phase: last.prevPhase,
    captures: last.prevCaptures,
    status: last.prevStatus,
    winner: last.prevWinner,
    endReason: last.prevEndReason,
    turnNumber: last.prevTurnNumber,
    moveNumber: Math.max(0, state.moveNumber - 1),
    history: state.history.slice(0, -1),
    updatedAt: Date.now(),
  };
}

export function totalCounters(state) {
  let s = state.captures.A + state.captures.B;
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) s += state.board[r][c];
  return s;
}

export function describeMove(entry) {
  if (!entry) return "";
  const p = entry.player === PLAYER_A ? "Player 1" : "Player 2";
  const parts = [`${p} sowed counter-clockwise with two-counter protection.`];
  if (entry.relays > 0) parts.push(`Relayed ${entry.relays} time${entry.relays === 1 ? "" : "s"}.`);
  if (entry.captured > 0) parts.push(`Captured ${entry.captured} seeds.`);
  else parts.push("No capture.");
  return parts.join(" ");
}
